import { useState, useEffect, useMemo, useRef } from 'react'
import {
  buildLockGraph,
  resolveLock,
  subscribeLockPages,
  collectPageKeysForTargets,
  type LockTarget,
} from '../services/checklistLockService'
import type { RepeatPageDoc, LockStateResult } from '../services/repeat/types'

export interface UseChecklistLockStateOptions {
  workspaceId?: string
  timeZone?: string
  now?: Date
}

export type { LockTarget }

export type ChecklistLockStateMap = Map<string, LockStateResult> & {
  pages: Map<string, RepeatPageDoc>
}

/**
 * useChecklistLockState — hook returning Map<itemId|periodKey, LockStateResult>
 */
export function useChecklistLockState<T extends { id?: unknown }>(
  items: ReadonlyArray<T>,
  targets: LockTarget[],
  options?: string | UseChecklistLockStateOptions,
): ChecklistLockStateMap {
  const opts: UseChecklistLockStateOptions =
    typeof options === 'string' ? { workspaceId: options } : options ?? {}

  const workspaceId = opts.workspaceId || ''
  const timeZone = opts.timeZone
  const now = opts.now
  const nowVal = now instanceof Date ? now.getTime() : typeof now === 'number' ? now : undefined

  const graph = useMemo(() => buildLockGraph(items), [items])
  const [pages, setPages] = useState<Map<string, RepeatPageDoc>>(() => new Map())

  // Target signature for stable dependency tracking
  const targetsSig = useMemo(
    () => targets.map((t) => `${t.itemId}:${t.periodKey}`).sort().join(';'),
    [targets],
  )

  // Signature of needed page keys across targets and graph
  const pageKeysSig = useMemo(() => {
    if (!workspaceId || targets.length === 0) return ''
    const needed = collectPageKeysForTargets(targets, graph, now, timeZone)
    return `${workspaceId}|${timeZone ?? ''}|${needed.map((entry) => `${entry.checklistType}:${entry.pageKey}`).sort().join(';')}`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, timeZone, targetsSig, graph, nowVal])

  const targetsRef = useRef(targets)
  const graphRef = useRef(graph)
  const nowRef = useRef(now)

  useEffect(() => {
    targetsRef.current = targets
    graphRef.current = graph
    nowRef.current = now
  })

  useEffect(() => {
    if (!workspaceId || !pageKeysSig) {
      return
    }

    const unsub = subscribeLockPages(
      workspaceId,
      targetsRef.current,
      graphRef.current,
      (updatedPages) => {
        setPages(updatedPages)
      },
      nowRef.current,
      timeZone,
    )

    return () => {
      unsub()
      // Keep last pages until new subscription delivers, avoiding flash of locked state
    }
  }, [workspaceId, pageKeysSig, timeZone])

  const results = useMemo(() => {
    const map = new Map<string, LockStateResult>()
    for (const target of targets) {
      const lock = resolveLock(target.itemId, target.periodKey, graph, pages, now, timeZone)
      map.set(`${target.itemId}|${target.periodKey}`, lock)
      map.set(`${target.itemId}:${target.periodKey}`, lock)
    }
    Object.defineProperty(map, 'pages', { value: pages, enumerable: false, writable: true })
    return map as ChecklistLockStateMap
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targets, graph, pages, nowVal, timeZone])

  return results
}
